import { useEffect, useRef, useState } from "react";
import { getSettings, sendChat } from "@/lib/api";
import { PaperPlaneTilt, X, Lightning, CircleNotch } from "@phosphor-icons/react";

/**
 * Standalone chat widget — works inside an iframe embed or preview modal.
 */
export default function ChatWidget({ onClose }) {
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [name, setName] = useState("Guest");
  const [nameSet, setNameSet] = useState(false);
  const [sessionId] = useState(() => {
    const existing = sessionStorage.getItem("viralbot-session");
    if (existing) return existing;
    const id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    sessionStorage.setItem("viralbot-session", id);
    return id;
  });
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    getSettings().then(s => {
      setSettings(s);
      setMessages([{ role: "assistant", content: s.chatbot_welcome }]);
    });
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    if (!input.trim() || loading) return;
    if (!nameSet && name === "Guest") {
      // Take the first message as their name if it looks like a name
    }
    const userMsg = { role: "user", content: input };
    setMessages(m => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await sendChat(sessionId, userMsg.content, name);
      setMessages(m => [...m, { role: "assistant", content: res.reply }]);
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Sorry — I had trouble connecting. Please email support@pepstyles.store." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col bg-arena-bg border border-arena-border" data-testid="chat-widget">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-arena-border bg-arena-surface">
        <div className="flex items-center gap-2">
          <Lightning size={18} weight="fill" className="text-arena-primary" />
          <div>
            <div className="font-heading text-sm font-bold">{settings?.chatbot_name || "PepBot"}</div>
            <div className="text-[10px] text-arena-success flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-arena-success animate-pulse-dot" />
              Online
            </div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-arena-muted hover:text-arena-danger" data-testid="close-widget">
            <X size={18} weight="bold" />
          </button>
        )}
      </div>

      {/* Name gate (compact) */}
      {!nameSet && (
        <div className="p-3 border-b border-arena-border bg-arena-surfaceHover">
          <div className="flex items-center gap-2">
            <input
              data-testid="widget-name-input"
              placeholder="Your name (optional)"
              value={name === "Guest" ? "" : name}
              onChange={e => setName(e.target.value || "Guest")}
              className="flex-1 bg-arena-bg border border-arena-border px-2 py-1.5 text-xs focus:border-arena-primary outline-none"
            />
            <button
              onClick={() => setNameSet(true)}
              data-testid="widget-name-continue"
              className="bg-arena-primary text-black px-3 py-1.5 text-[10px] tracking-widest uppercase font-bold"
            >
              Chat
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="widget-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-3 py-2 text-xs ${
              m.role === 'user'
                ? 'bg-arena-primary text-black'
                : 'bg-arena-surface text-arena-text border border-arena-border'
            }`}>
              <div className="whitespace-pre-wrap leading-relaxed">{m.content}</div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-arena-surface border border-arena-border px-3 py-2 text-xs text-arena-muted flex items-center gap-2">
              <CircleNotch size={12} weight="bold" className="animate-spin" />
              typing...
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t border-arena-border bg-arena-surface">
        <div className="flex items-center gap-2">
          <input
            data-testid="widget-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Ask about your order, shipping, returns..."
            className="flex-1 bg-arena-bg border border-arena-border px-3 py-2 text-xs focus:border-arena-primary outline-none"
            disabled={loading}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            data-testid="widget-send"
            className="bg-arena-primary text-black p-2 hover:bg-arena-primaryHover disabled:opacity-50"
          >
            <PaperPlaneTilt size={14} weight="fill" />
          </button>
        </div>
      </div>
    </div>
  );
}
